import { isEmpty } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyFetchUserProductStatusQuery } from "../../features/Feature_ABCD/Feature_ABCD_Api";
import {
  setProductStatusApiCalled,
  setProductStatusApiFail,
  setUserProductStatusDetails,
} from "../../features/Feature_ABCD/Feature_ABCD_Slice";
import {
  useLazyFetchAppPermissionQuery,
  useLazyLogoutApiQuery,
} from "../../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import {
  setAppPermission,
  setIsNoHolding,
} from "../../features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import { T_Update_App_Permission_Request } from "../../features/Feature_GeneralAppElements/GeneralAppElements_Types";
import { updateLoadingState } from "../../features/Feature_Loading/Loading_Slice";
import {
  useLazyGetCustomerLobDetailsQuery,
  useLazyGetCustomerLobDetailsV2Query,
  useLazyGetInternalTokenQuery,
} from "../../features/Feature_Login/Login_Api";
import { resetLoginData } from "../../features/Feature_Login/Login_Slice";
import { T_GetCustomerLobDetailsRes } from "../../features/Feature_Login/Login_Types";
import {
  E_ChannelOptions,
  J_FunctionalityCode,
  T_ApiResponse,
  T_CustomerLobDetails,
} from "../../globalTypes/GlobalTypes";
import useNativePermissionSync from "../../hooks/useNativePermissionSync";
import useProfileAndSaveCampaignApi from "../../hooks/useProfileAndSaveCampaignApi";
import useQuickActionMenu from "../../hooks/useQuickActionMenu";
import useSaveCrossSellConsent from "../../hooks/useSaveCrossSellConsent";
import {
  useAppDispatch,
  useAppSelector,
} from "../../redux-store/CustomReduxHooks";
import {
  saveCsUserId,
  setCustomerAndLobDetails,
  setLongSessionAuthToken,
} from "../../redux-store/reducers/CsAppSlice";
import { setError } from "../../redux-store/reducers/CsErrorSlice";
import { API_STATUS_CODE } from "../../utils/ApiEndpoints";
import { INTERNAL_AUTH_TOKEN, setCookie } from "../../utils/CSCookieHandler";
import cleverTap, { triggerCleverTapEvent } from "../../utils/CleverTap";
import CsLocalStorageUtils from "../../utils/CsLocalStorageUtils";
import CsSessionStorageUtils from "../../utils/CsSessionStorageUtils";
import {
  ABCDHoldingsLobCode,
  onUserLoginCleverTapStatic,
  SCREEN_TYPE,
} from "../../utils/constants";
import { createUpdatePermissionData } from "../../utils/helper";
import { R_HomeRoutes } from "../../utils/Routes";
import {
  getAuthPassed,
  getShowGuardFallback,
  resolveMobileNumber,
} from "./AuthGuard.helpers";

type T_UseAuthGuardBootstrapProps = {
  showCustomLoading?: boolean;
  initFlag?: boolean;
};

const useAuthGuardBootstrap = ({
  showCustomLoading = false,
  initFlag = true,
}: T_UseAuthGuardBootstrapProps) => {
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const tokenDetails = csAppSlice?.tokenDetails;
  const userDetails = csAppSlice?.userDetails;
  const loginSlice = useAppSelector((state) => state.LoginSlice);
  const SsoSlice = useAppSelector((state) => state.SsoSlice.ssoData);
  const SecondaryChannelSlice = useAppSelector(
    (state) => state.SecondaryChannelSlice,
  );
  const internalAuthToken = useAppSelector(
    (state) => state.CsAppSlice?.tokenDetails?.internal,
  );
  const { isPseudoLoggedIn } = useAppSelector(
    (state) => state.PseudoLoginSlice,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const appDispatch = useAppDispatch();
  const [decidingNextRoute, setDecidingNextRoute] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [popupError, setPopupError] = useState<any>(null);
  const [csLobDetails, setCsLobDetails] =
    useState<T_ApiResponse<T_GetCustomerLobDetailsRes>>();
  const [mobileNumber, setMobileNumber] = useState<string | undefined>();
  const [openBlockedUserPopup, setOpenBlockedUserPopup] =
    useState<boolean>(false);
  const [getCustomerLobDetails] = useLazyGetCustomerLobDetailsQuery();
  const [getCustomerLobDetailsV2] = useLazyGetCustomerLobDetailsV2Query();
  const [getInternalToken] = useLazyGetInternalTokenQuery();
  const [getLogOutApi] = useLazyLogoutApiQuery();
  const getProfileDetailApi = useProfileAndSaveCampaignApi();
  const [fetchAppPermission] = useLazyFetchAppPermissionQuery();
  useQuickActionMenu();
  const [fetchUserProductStatusApi] = useLazyFetchUserProductStatusQuery();
  const { callSaveCrossSellConsentApi } = useSaveCrossSellConsent();
  const { syncNativePermissions } = useNativePermissionSync();
  const isExecuting = useRef(false);
  const permissionSyncCalledRef = useRef(false);

  const manageLoading = useCallback(
    (showLoading: boolean = false) => {
      if (showCustomLoading) {
        return;
      }
      appDispatch(updateLoadingState(showLoading));
    },
    [appDispatch, updateLoadingState, showCustomLoading],
  );

  const showError = (err: Error) => {
    appDispatch(setError({ error: err, message: err.message }));
  };

  const gotoLogin = () => {
    manageLoading(false);
    appDispatch(resetLoginData());
    navigate("/login", { replace: true });
  };

  const saveCsUserIdToLocalStorage = (csUserId: string) => {
    CsLocalStorageUtils.set("CS_USER_ID", csUserId);
  };

  const gotoNoHoldingsPage = () => {
    appDispatch(setIsNoHolding(true));
    navigate(R_HomeRoutes.home, { replace: true });
    manageLoading(false);
  };

  const navigateToUrl = (
    userDetailsValue: T_CustomerLobDetails | undefined,
  ) => {
    if (!userDetailsValue?.IS_USER_REGISTERED) {
      navigate(R_HomeRoutes.home, { replace: true });
    } else if (csAppSlice?.channel === E_ChannelOptions.SECONDARY_CHANNEL) {
      navigate(SecondaryChannelSlice.constructedAppUrl, {
        state: SecondaryChannelSlice.dynamicState,
        replace: true,
      });
    } else if (isEmpty(userDetailsValue.customerAndLobDetails)) {
      gotoNoHoldingsPage();
    }
    manageLoading(false);
    setDecidingNextRoute(false);
  };

  const getAppPermissions = async (userId: string) => {
    try {
      let mobileNo = resolveMobileNumber({
        channel: csAppSlice?.channel,
        ssoMobileNo: SsoSlice.mobileNo,
        loginMobileNo: loginSlice.mobilenumber,
        localStorageMobileNo: mobileNumber,
      });
      const response = await fetchAppPermission({
        mobileNo: mobileNo || "",
        csUserId: userId,
      });

      if (
        response.data?.code === API_STATUS_CODE.SUCCESS &&
        !isEmpty(response.data?.payload)
      ) {
        let res = response.data?.payload;
        let updatePermissionData: T_Update_App_Permission_Request =
          createUpdatePermissionData(res);
        appDispatch(setAppPermission(updatePermissionData));
      }
    } catch (e) {
      appDispatch(
        setError({
          error: new Error(),
          message: "failed",
        }),
      );
    }
  };

  const fetchUserProductStatusApiFun = async (
    csLOBDe: T_GetCustomerLobDetailsRes,
    mobile: string,
  ) => {
    if (isEmpty(csLOBDe?.customerAndLobDetails)) {
      return;
    }
    let abcdHolding = csLOBDe?.customerAndLobDetails?.filter((c) => {
      return c.LOB === ABCDHoldingsLobCode;
    });

    if (isEmpty(abcdHolding)) {
      return;
    } else {
      fetchUserProductStatusApi({
        mobileNumber: mobile,
      }).then((res) => {
        appDispatch(setProductStatusApiCalled(true));
        if (
          res.error ||
          res.isError ||
          res.data?.code !== API_STATUS_CODE.SUCCESS
        ) {
          appDispatch(setProductStatusApiFail(true));
        }
        if (res.data?.code === API_STATUS_CODE.SUCCESS) {
          appDispatch(setUserProductStatusDetails(res.data?.payload?.data));
          appDispatch(setProductStatusApiFail(false));
        }
      });
    }
  };

  const getInternalTokenApi = (
    csLOBDe: T_ApiResponse<T_GetCustomerLobDetailsRes>,
  ) => {
    manageLoading(true);
    if (csLOBDe?.payload?.CS_USER_ID) {
      getInternalToken(csLOBDe?.payload?.CS_USER_ID).then((tokenResponse) => {
        if (tokenResponse.data?.code === API_STATUS_CODE.SUCCESS) {
          manageLoading(false);
          setCookie(
            INTERNAL_AUTH_TOKEN,
            tokenResponse.data?.payload?.authToken,
            1,
          );
          getProfileDetailApi(csLOBDe?.payload);
          getAppPermissions(csLOBDe?.payload.CS_USER_ID);

          // if (!permissionSyncCalledRef.current) {
          //   permissionSyncCalledRef.current = true;
          //   const mobileNo = allowedChannels.includes(csAppSlice?.channel)
          //     ? SsoSlice.mobileNo
          //     : loginSlice.mobilenumber || mobileNumber || "";
          //   syncNativePermissions(mobileNo, csLOBDe?.payload.CS_USER_ID ?? "");
          // }
          if (!csLOBDe?.payload) {
            gotoNoHoldingsPage();
            return;
          }
          if (
            !isEmpty(SsoSlice?.functionality) &&
            csAppSlice?.channel === E_ChannelOptions.ONE_ABC
          ) {
            const funcObj = J_FunctionalityCode.find(
              (j) => j.funcCode === (SsoSlice?.functionality || ""),
            );
            let filteredCustomerLobArr = { ...csLOBDe?.payload };
            if (funcObj && !isEmpty(csLOBDe?.payload?.customerAndLobDetails)) {
              filteredCustomerLobArr.customerAndLobDetails =
                csLOBDe?.payload?.customerAndLobDetails?.filter((c) =>
                  funcObj?.lobCode?.includes(c.LOB_CODE),
                );
            }
            appDispatch(setCustomerAndLobDetails(filteredCustomerLobArr));
          } else {
            appDispatch(setCustomerAndLobDetails(csLOBDe?.payload));
          }
          saveCsUserIdToLocalStorage(csLOBDe?.payload?.CS_USER_ID);
          appDispatch(
            setLongSessionAuthToken(tokenResponse.data.payload.authToken),
          );
          navigateToUrl(csLOBDe?.payload);
        } else if (
          tokenResponse.data?.code === API_STATUS_CODE.BUSINESS_ERROR
        ) {
          manageLoading(false);
          setPopupError(true);
          setShowPopup(true);
        } else if (tokenResponse.error) {
          showError(tokenResponse.error as Error);
          gotoLogin();
          return;
        } else {
          manageLoading(false);
        }
      });
    } else {
      manageLoading(false);
    }
  };

  const logoutPreviousSession = async () => {
    setShowPopup(false);
    setPopupError(false);
    manageLoading(true);
    await getLogOutApi({
      csUserId: "" + userDetails?.CS_USER_ID,
    });
    if (csLobDetails) {
      getInternalTokenApi(csLobDetails);
    }
  };

  const init = useCallback(async () => {
    if (isEmpty(userDetails?.customerAndLobDetails)) {
      appDispatch(setIsNoHolding(true));
    } else {
      appDispatch(setIsNoHolding(false));
    }
    if (!userDetails?.customerAndLobDetails && loginSlice.isUserLoggedIn) {
      if (isExecuting.current) {
        return;
      }
      isExecuting.current = true;
      try {
        CsSessionStorageUtils.clear("ACTIVE_DASHBOARD_TAB");
        let mobileNoFromStorage = CsLocalStorageUtils.get("MOBILE");

        const mobile = resolveMobileNumber({
          channel: csAppSlice?.channel,
          ssoMobileNo: SsoSlice.mobileNo,
          loginMobileNo: loginSlice.mobilenumber,
          localStorageMobileNo: mobileNoFromStorage,
        });
        if (!mobile) {
          gotoLogin();
          return;
        }
        setMobileNumber(mobile);
        manageLoading(true);

        let fetchCustomerLobDetails = getCustomerLobDetails;
        if (
          csAppSlice?.channel !== "CSAPP" ||
          !isEmpty(csAppSlice.NativeDeviceInfo?.deviceName) ||
          isPseudoLoggedIn
        ) {
          fetchCustomerLobDetails = getCustomerLobDetailsV2;
        }
        const response = await fetchCustomerLobDetails({ mobile: mobile });
        if (
          response?.data?.code === API_STATUS_CODE.BUSINESS_ERROR &&
          isEmpty(response?.data?.payload)
        ) {
          setOpenBlockedUserPopup(true);
          manageLoading(false);
          return;
        }
        if (!response.data) {
          showError(new Error("error"));
          gotoLogin();
          isExecuting.current = false;
          return;
        }

        setCsLobDetails(response?.data);
        if (
          response.data?.code === API_STATUS_CODE.SUCCESS &&
          !isEmpty(SsoSlice) &&
          !response?.data?.payload?.IS_USER_REGISTERED
        ) {
          callSaveCrossSellConsentApi(
            mobile || "",
            response?.data?.payload?.customerAndLobDetails,
          );
        }
        setDecidingNextRoute(true);
        if (response.error || !response.data?.payload?.customerAndLobDetails) {
          let onUserLoginCleverTapData = { ...onUserLoginCleverTapStatic };
          onUserLoginCleverTapData["Identity"] = "+91" + mobile;
          onUserLoginCleverTapData["MSG-push"] = !isEmpty(
            csAppSlice?.NativeDeviceInfo?.deviceName,
          );
          cleverTap.onUserLogin.push({ Site: onUserLoginCleverTapData });
          triggerCleverTapEvent("onUserLogin", {
            ScreenType: SCREEN_TYPE.DashboardScreenType,
            ScreenName: "Servicing Homescreen",
            isPrevPageNameRequired: true,
            ...onUserLoginCleverTapData,
            MSG_email: onUserLoginCleverTapData["MSG-email"],
            MSG_push: onUserLoginCleverTapData["MSG-push"],
            MSG_sms: onUserLoginCleverTapData["MSG-sms"],
            MSG_whatsapp: onUserLoginCleverTapData["MSG-whatsapp"],
          });
          showError(new Error("error"));
          gotoLogin();
          isExecuting.current = false;
          return;
        }
        if (response.data?.code === API_STATUS_CODE.SUCCESS) {
          appDispatch(saveCsUserId(response.data.payload.CS_USER_ID));
          await fetchUserProductStatusApiFun(response.data?.payload, mobile);
          if (csAppSlice?.tokenDetails?.internal?.longSessionAuthToken) {
            getProfileDetailApi(response.data?.payload);

            appDispatch(setCustomerAndLobDetails(response.data.payload));
            navigateToUrl(response.data?.payload);
            isExecuting.current = false;
            return;
          }
          getInternalTokenApi(response.data);
        } else {
          manageLoading(false);
          showError(new Error(JSON.stringify(response.data)));
        }
      } finally {
        isExecuting.current = false;
      }
    } else if (
      !loginSlice.isUserLoggedIn &&
      !loginSlice.isOtpVerificationDone
    ) {
      gotoLogin();
    } else if (
      !userDetails?.IS_USER_REGISTERED &&
      csAppSlice?.channel != E_ChannelOptions.ONE_ABC &&
      csAppSlice?.tokenDetails?.internal?.longSessionAuthToken
    ) {
      navigateToUrl(userDetails);
    } else {
      manageLoading(false);
      setDecidingNextRoute(false);
    }
  }, [userDetails?.customerAndLobDetails, internalAuthToken]);

  useEffect(() => {
    if (initFlag) {
      init();
    }
  }, [userDetails?.customerAndLobDetails, internalAuthToken, initFlag]);

  const authPassed = getAuthPassed({
    popupError,
    customerAndLobDetails: userDetails?.customerAndLobDetails,
    longSessionAuthToken: tokenDetails?.internal.longSessionAuthToken,
    decidingNextRoute,
  });

  const showGuardFallback = getShowGuardFallback({
    pathname: location.pathname,
    customerAndLobDetails: userDetails?.customerAndLobDetails,
    longSessionAuthToken: tokenDetails?.internal.longSessionAuthToken,
    decidingNextRoute,
  });

  const handleBlockedUserPopupClose = () => {
    setOpenBlockedUserPopup(false);
    gotoLogin();
  };

  const triggerUserLogout = () => {
    appDispatch({ type: "USER_LOGOUT" });
  };

  return {
    authPassed,
    showGuardFallback,
    popupError,
    showPopup,
    openBlockedUserPopup,
    logoutPreviousSession,
    handleBlockedUserPopupClose,
    triggerUserLogout,
  };
};

export default useAuthGuardBootstrap;
